import { Flex } from "@chakra-ui/layout";
import type { SystemProps } from "@chakra-ui/system";
import { Link } from "react-router-dom";
import { useState } from "react";
import styled from "styled-components";
import type { Consultant } from "@models/Consultant";
import { appTheme } from "@styles/Theme";
import { ellipsis } from "@utils/utils";
import ConsultantImage from "./ConsultantImage";
import { Tag } from "./Tag";
import { EditLink } from "./text/EditLink";

// TODO: Phase 5 - Import routes from routes.ts
const routes = {
	profile: "/konsultprofil/:id",
};

interface Props {
	consultant: Consultant;
	className?: string;
	hasCustomerExperience?: boolean;
	size?: "s" | "m";
	declined?: boolean;
	direction?: "row" | "column";
	align?: SystemProps["alignItems"];
	openProfileInNewTab?: boolean;
}

export const ConsultantProfile: React.FC<Props> = ({
	consultant,
	className,
	size = "m",
	hasCustomerExperience,
	declined,
	direction = "row",
	align = "flex-start",
	openProfileInNewTab = false,
}) => {
	const [showFullPresentation, setShowFullPresentation] = useState(false);

	const {
		str: presentation,
		isEllipsed,
		origStr: fullPresentation,
	} = ellipsis(consultant.profile?.presentation || "Presentation saknas", size === "m" ? 180 : 100);

	return (
		<Styles className={`${className} size-${size}`}>
			<Flex gap={3} align={align} direction={direction}>
				<ConsultantImage
					name={consultant.firstName + " " + consultant.lastName}
					url={consultant.profile?.profilePicture}
					size={size === "m" ? "125px" : "70px"}
				/>
				<Flex direction={"column"} className="profile-body" align={align} gap={3}>
					<h3 style={{ textDecoration: declined ? "line-through" : "" }}>
						{consultant.firstName} {consultant.lastName}
					</h3>
					{hasCustomerExperience && (
						<div>
							<Tag background="orange" textColor="white" text="Kunderfarenhet" />
						</div>
					)}
					<p
						className={!consultant.profile?.presentation ? "missing-presentation" : ""}
						style={{ textAlign: align === "center" ? "center" : undefined }}
					>
						{showFullPresentation ? fullPresentation : presentation}
					</p>

					{isEllipsed && (
						<EditLink onClick={() => setShowFullPresentation(!showFullPresentation)}>
							{showFullPresentation ? "Visa mindre" : "Visa mer"}
						</EditLink>
					)}

					<Link to={routes.profile.replace(":id", consultant.id)} target={openProfileInNewTab ? "_blank" : undefined} rel="noreferrer" style={{ textDecoration: "underline " }}>
						Visa profil
					</Link>
				</Flex>
			</Flex>
		</Styles>
	);
};

const Styles = styled.div`
	width: 100%;

	.profile-body {
		* {
			margin: 0;
		}

		p.missing-presentation {
			color: ${(props) => props.theme.colors.textDimmed};
		}

		h3 {
			font-style: normal;
			font-weight: 600;
			font-size: 16px;
			line-height: 19px;
			color: ${appTheme.colors.darker};
		}
		p {
			font-size: 14px;
			font-weight: 400;
			line-height: 17px;
			letter-spacing: 0em;
			text-align: left;
			color: ${appTheme.colors.darker};
		}

		a {
			font-style: normal;
			font-weight: 600;
			font-size: 14px;
			line-height: 17px;
			text-decoration-line: underline;
			color: ${appTheme.colors.darker};
		}
	}

	&.size-s {
		gap: 1rem;

		.round-profile {
			width: 70px;
			height: 70px;
			min-width: 70px;
			min-height: 70px;
		}
	}
`;
